import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import * as Lucide from "lucide-react";
import {
    X,
    Copy,
    CheckCheck,
    ExternalLink,
    Download,
    Trash2,
    AlertTriangle,
    ChevronDown,
    ChevronUp,
    Mail,
    User,
    Hash,
    Globe,
    Clock,
    Quote,
} from "lucide-react";
import { QUESTIONS, REACTION_TOASTS } from "../../lib/survey-schema";
import { getResponseDetail, deleteResponse } from "../adminApi";
import { Pill } from "./Pill";
import {
    individualPriceLabel,
    collegePriceLabel,
} from "../labels";

function fmtFull(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    } catch {
        return iso;
    }
}

function scoreColor(s) {
    if (s == null) return "#6B7FA3";
    if (s >= 70) return "#10B981";
    if (s >= 50) return "#F59E0B";
    return "#EF4444";
}

function getOptionMeta(questionId, value) {
    const q = QUESTIONS.find((x) => x.id === questionId);
    if (!q || !q.options) return null;
    return q.options.find((o) => o.value === value) || null;
}

function LucideOpt({ name, size = 16, className = "" }) {
    if (!name) return null;
    const Icon = Lucide[name];
    if (!Icon) return null;
    return <Icon size={size} className={className} />;
}

/**
 * Right-side slide-in drawer rendering the full case file for a survey
 * response: derived snapshot, all 10 answers, metadata, danger zone.
 *
 * Lazy-loads via GET /api/admin/responses/:id on first open.
 */
export function ResponseDrawer({ responseId, open, onClose, onDeleted }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [dangerOpen, setDangerOpen] = useState(false);

    useEffect(() => {
        if (!open || !responseId) return undefined;
        let alive = true;
        setLoading(true);
        setErr(null);
        setData(null);
        getResponseDetail(responseId)
            .then((d) => alive && setData(d))
            .catch((e) =>
                alive &&
                setErr(e?.response?.data?.detail || e?.message || "Failed to load"),
            )
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [open, responseId]);

    // Esc closes
    useEffect(() => {
        if (!open) return undefined;
        function onKey(e) {
            if (e.key === "Escape") onClose?.();
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    return (
        <AnimatePresence>
            {open ? (
                <motion.div
                    key="drawer-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    className="fixed inset-0 z-[70] bg-klareet-NAVY/70 backdrop-blur-sm"
                    onClick={onClose}
                    data-testid="drawer-backdrop"
                >
                    <motion.aside
                        key="drawer-panel"
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{
                            type: "tween",
                            duration: 0.28,
                            ease: [0.2, 0.8, 0.2, 1],
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-0 right-0 h-full w-full sm:w-[640px] bg-klareet-NAVY border-l border-klareet-bor shadow-2xl flex flex-col"
                        role="dialog"
                        aria-label="Response detail"
                        data-testid="response-drawer"
                    >
                        <DrawerHeader data={data} onClose={onClose} loading={loading} />
                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                            {loading ? (
                                <div className="grid place-items-center py-20">
                                    <div className="inline-block w-8 h-8 rounded-full border-2 border-klareet-bor border-t-klareet-T animate-spin" />
                                </div>
                            ) : err ? (
                                <div className="rounded-2xl border border-klareet-RED/40 bg-klareet-RED/10 text-klareet-RED p-5 text-sm">
                                    {err}
                                </div>
                            ) : data ? (
                                <>
                                    <SnapshotCard data={data} />
                                    <AllAnswers data={data} />
                                    <MetadataBlock data={data} />
                                    <DangerZone
                                        open={dangerOpen}
                                        onToggle={() => setDangerOpen((v) => !v)}
                                        onDelete={() => setConfirmingDelete(true)}
                                    />
                                </>
                            ) : null}
                        </div>
                    </motion.aside>
                </motion.div>
            ) : null}
            {confirmingDelete && data ? (
                <DeleteModal
                    key="delete-modal"
                    id={data.id}
                    onCancel={() => setConfirmingDelete(false)}
                    onDeleted={() => {
                        setConfirmingDelete(false);
                        onDeleted?.(data.id);
                        onClose?.();
                    }}
                />
            ) : null}
        </AnimatePresence>
    );
}

function DrawerHeader({ data, onClose, loading }) {
    const [copied, setCopied] = useState(false);
    function copy(text) {
        navigator.clipboard?.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
        });
    }
    return (
        <div className="px-6 pt-5 pb-4 border-b border-klareet-bor">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-klareet-T2">
                        Case file
                    </div>
                    <div
                        className="mt-1 text-sm font-mono text-klareet-MUT"
                        data-testid="drawer-timestamp"
                    >
                        {fmtFull(data?.created_at)}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-klareet-bor text-klareet-MUT hover:text-klareet-TEXT hover:border-klareet-T transition-colors"
                    aria-label="Close drawer"
                    data-testid="drawer-close-btn"
                >
                    <X size={16} />
                </button>
            </div>

            {!loading && data ? (
                <div className="mt-4 flex flex-wrap items-center gap-2">
                    <Pill value={data.persona} kind="persona" />
                    <Pill value={data.pain} kind="pain" />
                    <Pill value={data.waitlist} kind="waitlist" />
                    <button
                        type="button"
                        onClick={() => copy(data.share_code || "")}
                        className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-klareet-bor bg-klareet-card font-mono text-xs text-klareet-T2 hover:border-klareet-T transition-colors"
                        data-testid="drawer-share-copy"
                    >
                        {data.share_code}
                        {copied ? <CheckCheck size={12} /> : <Copy size={12} />}
                    </button>
                </div>
            ) : null}
        </div>
    );
}

function SnapshotCard({ data }) {
    const snap = data.snapshot || {};
    const arch = snap.archetype || {};
    const score = snap.score;
    const reportHref = `/snapshot/${data.id}?code=${data.share_code}`;
    const dashboardHref = `/r/${data.share_code}`;
    const pdfHref = `/report/${data.id}?code=${data.share_code}`;

    return (
        <section
            className="rounded-2xl border border-klareet-T/40 bg-klareet-card p-5 space-y-4"
            data-testid="drawer-snapshot"
        >
            <div className="flex items-start gap-4">
                <div
                    className="text-4xl shrink-0 leading-none"
                    aria-hidden
                    style={{ color: arch.color || "#0D9488" }}
                >
                    {arch.emoji || "✦"}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-klareet-MUT">
                        Archetype
                    </div>
                    <div
                        className="text-2xl md:text-3xl font-bold tracking-tight mt-0.5"
                        style={{ color: arch.color || "#0D9488" }}
                    >
                        {arch.name || "—"}
                    </div>
                    {arch.line ? (
                        <p className="mt-2 text-sm italic text-klareet-TEXT/85 leading-relaxed">
                            "{arch.line}"
                        </p>
                    ) : null}
                </div>
                <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-klareet-MUT">
                        Score
                    </div>
                    <div
                        className="font-mono text-3xl md:text-4xl font-bold tabular-nums"
                        style={{ color: scoreColor(score) }}
                        data-testid="drawer-score"
                    >
                        {score ?? "—"}
                        <span className="text-klareet-MUT text-sm font-normal">
                            /100
                        </span>
                    </div>
                </div>
            </div>

            {snap.strength ? (
                <div className="rounded-xl border border-klareet-bor bg-klareet-NAVY/40 p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-klareet-MUT mb-1">
                        Strength
                    </div>
                    <p className="text-sm italic text-klareet-TEXT/95 leading-relaxed">
                        {snap.strength}
                    </p>
                </div>
            ) : null}

            {snap.week_one_action ? (
                <div className="rounded-xl border border-klareet-bor bg-klareet-NAVY/40 p-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-klareet-MUT mb-1">
                        Week 1 action
                    </div>
                    <p className="text-sm text-klareet-TEXT leading-relaxed">
                        {snap.week_one_action}
                    </p>
                </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
                <a
                    href={reportHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-klareet-bor bg-klareet-NAVY text-xs text-klareet-T2 hover:border-klareet-T transition-colors"
                    data-testid="drawer-link-snapshot"
                >
                    <ExternalLink size={12} />
                    View public Snapshot
                </a>
                <a
                    href={dashboardHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-klareet-bor bg-klareet-NAVY text-xs text-klareet-T2 hover:border-klareet-T transition-colors"
                    data-testid="drawer-link-dashboard"
                >
                    <ExternalLink size={12} />
                    Open public dashboard
                </a>
                <a
                    href={pdfHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-klareet-bor bg-klareet-NAVY text-xs text-klareet-T2 hover:border-klareet-T transition-colors"
                    data-testid="drawer-link-pdf"
                >
                    <Download size={12} />
                    Download as PDF
                </a>
            </div>
        </section>
    );
}

function AnswerCard({ qNum, q, children }) {
    return (
        <article className="rounded-2xl border border-klareet-bor bg-klareet-card p-4">
            <div className="flex items-start gap-2 mb-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-klareet-T2 px-2 py-0.5 rounded-full border border-klareet-T/40 bg-klareet-T/5 shrink-0">
                    Q{qNum}
                </span>
                <h4 className="text-sm text-klareet-TEXT/85 leading-snug">
                    {q.question}
                </h4>
            </div>
            <div className="pl-12 -mt-1">{children}</div>
        </article>
    );
}

function CardOption({ qId, value }) {
    const opt = getOptionMeta(qId, value);
    if (!opt) {
        return (
            <span className="text-klareet-DIM text-sm italic">(left blank)</span>
        );
    }
    return (
        <div className="inline-flex items-start gap-3 rounded-xl border border-klareet-bor bg-klareet-NAVY/40 px-3 py-2 max-w-full">
            <LucideOpt
                name={opt.icon}
                size={18}
                className="text-klareet-T2 shrink-0 mt-0.5"
            />
            <div className="min-w-0">
                <div className="text-sm text-klareet-TEXT font-medium leading-snug">
                    {opt.title}
                </div>
                {opt.sub ? (
                    <div className="text-[11px] text-klareet-MUT mt-0.5">
                        {opt.sub}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function PriceOption({ qId, value, labelFn }) {
    const opt = getOptionMeta(qId, value);
    if (!value) {
        return (
            <span className="text-klareet-DIM text-sm italic">(skipped)</span>
        );
    }
    return (
        <div className="inline-flex items-center gap-3 rounded-xl border border-klareet-bor bg-klareet-NAVY/40 px-3 py-2">
            <LucideOpt
                name={opt?.icon}
                size={18}
                className="text-klareet-T2 shrink-0"
            />
            <div>
                <div className="text-sm text-klareet-TEXT font-medium">
                    {labelFn(value)}
                </div>
                {opt?.sub ? (
                    <div className="text-[11px] text-klareet-MUT mt-0.5">
                        {opt.sub}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

function AllAnswers({ data }) {
    const reactions = QUESTIONS.find((q) => q.id === "klareet_appeal")?.reactions || {};
    const personaQ = QUESTIONS.find((q) => q.id === "persona");
    const painQ = QUESTIONS.find((q) => q.id === "pain");
    const toolsQ = QUESTIONS.find((q) => q.id === "tools");
    const gapQ = QUESTIONS.find((q) => q.id === "gap");
    const appealQ = QUESTIONS.find((q) => q.id === "klareet_appeal");
    const priceQ = QUESTIONS.find((q) => q.id === "price_individual");
    const collegeQ = QUESTIONS.find((q) => q.id === "college_price");
    const oneQ = QUESTIONS.find((q) => q.id === "one_thing");
    const waitlistQ = QUESTIONS.find((q) => q.id === "waitlist");
    const emailQ = QUESTIONS.find((q) => q.id === "email");

    const appealReaction = data.klareet_appeal ? reactions[data.klareet_appeal] : null;
    const tools = data.tools || [];

    return (
        <section
            className="space-y-3"
            data-testid="drawer-answers"
            aria-label="All answers"
        >
            <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-klareet-MUT mb-2">
                All answers (Q1–Q10)
            </div>

            <AnswerCard qNum={1} q={personaQ}>
                <CardOption qId="persona" value={data.persona} />
            </AnswerCard>

            <AnswerCard qNum={2} q={painQ}>
                <CardOption qId="pain" value={data.pain} />
            </AnswerCard>

            <AnswerCard qNum={3} q={toolsQ}>
                {tools.length === 0 ? (
                    <span className="text-klareet-DIM text-sm italic">
                        (skipped)
                    </span>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {tools.map((t) => {
                            const opt = getOptionMeta("tools", t);
                            return (
                                <span
                                    key={t}
                                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-klareet-bor bg-klareet-NAVY/40 text-xs text-klareet-TEXT"
                                >
                                    <LucideOpt
                                        name={opt?.icon}
                                        size={12}
                                        className="text-klareet-T2"
                                    />
                                    {opt?.title || t}
                                </span>
                            );
                        })}
                    </div>
                )}
            </AnswerCard>

            <AnswerCard qNum={4} q={gapQ}>
                {data.gap ? (
                    <CardOption qId="gap" value={data.gap} />
                ) : (
                    <span className="text-klareet-DIM text-sm italic">
                        (skipped — no tools tried)
                    </span>
                )}
            </AnswerCard>

            <AnswerCard qNum={5} q={appealQ}>
                <div className="flex items-center gap-3">
                    <span
                        className="font-mono text-3xl font-bold tabular-nums text-klareet-TEXT"
                        style={{ color: scoreColor(data.klareet_appeal * 10) }}
                    >
                        {data.klareet_appeal ?? "—"}
                        <span className="text-klareet-MUT text-base font-normal">
                            /10
                        </span>
                    </span>
                    {appealReaction ? (
                        <span className="text-klareet-MUT text-sm">
                            <span className="text-2xl mr-1">
                                {appealReaction.emoji}
                            </span>
                            {appealReaction.text}
                        </span>
                    ) : null}
                </div>
            </AnswerCard>

            <AnswerCard qNum={6} q={priceQ}>
                <PriceOption
                    qId="price_individual"
                    value={data.price_individual}
                    labelFn={individualPriceLabel}
                />
            </AnswerCard>

            <AnswerCard qNum={7} q={collegeQ}>
                {data.college_price ? (
                    <PriceOption
                        qId="college_price"
                        value={data.college_price}
                        labelFn={collegePriceLabel}
                    />
                ) : (
                    <span className="text-klareet-DIM text-sm italic">
                        (skipped — not a student persona)
                    </span>
                )}
            </AnswerCard>

            <AnswerCard qNum={8} q={oneQ}>
                {data.one_thing ? (
                    <blockquote className="border-l-2 border-klareet-T2 pl-3 italic text-klareet-TEXT leading-relaxed">
                        <Quote
                            size={12}
                            className="inline text-klareet-T2 mr-1 opacity-60"
                        />
                        {data.one_thing}
                    </blockquote>
                ) : (
                    <span className="text-klareet-DIM text-sm italic">
                        (left blank)
                    </span>
                )}
            </AnswerCard>

            <AnswerCard qNum={9} q={waitlistQ}>
                <CardOption qId="waitlist" value={data.waitlist} />
            </AnswerCard>

            <AnswerCard qNum={10} q={emailQ}>
                {data.email ? (
                    <span className="font-mono text-sm text-klareet-TEXT">
                        {data.email}
                    </span>
                ) : (
                    <span className="text-klareet-DIM text-sm italic">
                        (left blank)
                    </span>
                )}
            </AnswerCard>
        </section>
    );
}

function MetadataKV({ icon: Icon, label, children, mono = false, testId }) {
    return (
        <div className="flex items-start gap-3 py-2">
            <Icon size={14} className="text-klareet-MUT mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-klareet-MUT">
                    {label}
                </div>
                <div
                    className={`text-sm text-klareet-TEXT mt-0.5 break-all ${mono ? "font-mono" : ""}`}
                    data-testid={testId}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}

function MetadataBlock({ data }) {
    const [revealHash, setRevealHash] = useState(false);
    const ipShort = (data.ip_hash || "").slice(0, 8);
    return (
        <section
            className="rounded-2xl border border-klareet-bor bg-klareet-card p-5"
            data-testid="drawer-metadata"
        >
            <div className="text-[11px] uppercase tracking-[0.18em] font-mono text-klareet-MUT mb-3">
                Referral & metadata
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                <MetadataKV icon={Hash} label="Share code" mono>
                    {data.share_code || "—"}
                </MetadataKV>
                <MetadataKV icon={Hash} label="Referred by" mono>
                    {data.referred_by || "—"}
                </MetadataKV>
                <MetadataKV icon={Hash} label="Referrals">
                    <span className="font-mono">
                        {data.referral_count ?? 0}/20
                    </span>
                    {data.free_year_earned ? (
                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-klareet-GREEN/40 bg-klareet-GREEN/10 text-[10px] uppercase tracking-[0.18em] font-mono text-klareet-GREEN">
                            ★ Earned
                        </span>
                    ) : null}
                </MetadataKV>
                <MetadataKV icon={Clock} label="Earned at" mono>
                    {data.free_year_earned ? fmtFull(data.earned_at) : "—"}
                </MetadataKV>
                <MetadataKV icon={User} label="Name">
                    {data.name || "—"}
                </MetadataKV>
                <MetadataKV icon={Mail} label="Email">
                    {data.email || "—"}
                </MetadataKV>
                {data.email_lower && data.email_lower !== (data.email || "").toLowerCase() ? (
                    <MetadataKV icon={Mail} label="Email (lower)" mono>
                        {data.email_lower}
                    </MetadataKV>
                ) : null}
                <MetadataKV icon={Clock} label="Created" mono>
                    {fmtFull(data.created_at)}
                </MetadataKV>
                <MetadataKV icon={Globe} label="Referrer URL">
                    {data.referrer || "—"}
                </MetadataKV>
                <MetadataKV icon={Globe} label="User agent" testId="drawer-ua">
                    <span title={data.user_agent || ""}>
                        {(data.user_agent || "—").length > 60
                            ? (data.user_agent || "").slice(0, 60) + "…"
                            : data.user_agent || "—"}
                    </span>
                </MetadataKV>
                <MetadataKV icon={Hash} label="IP hash" mono>
                    <button
                        type="button"
                        onClick={() => setRevealHash((v) => !v)}
                        className="text-left hover:text-klareet-T2 transition-colors"
                        data-testid="drawer-ip-hash"
                    >
                        {revealHash
                            ? data.ip_hash || "—"
                            : ipShort
                              ? `${ipShort}…`
                              : "—"}
                    </button>
                </MetadataKV>
            </div>
        </section>
    );
}

function DangerZone({ open, onToggle, onDelete }) {
    return (
        <section className="rounded-2xl border border-klareet-RED/30 bg-klareet-RED/5 p-5">
            <button
                type="button"
                onClick={onToggle}
                className="w-full flex items-center justify-between gap-2 text-left"
                data-testid="drawer-danger-toggle"
            >
                <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-mono text-klareet-RED">
                    <AlertTriangle size={14} />
                    Danger zone
                </span>
                {open ? (
                    <ChevronUp size={14} className="text-klareet-RED" />
                ) : (
                    <ChevronDown size={14} className="text-klareet-RED" />
                )}
            </button>
            {open ? (
                <div className="mt-4 flex items-center justify-between gap-3">
                    <p className="text-xs text-klareet-MUT max-w-md">
                        Permanently delete this response. The respondent's email
                        cap counter, share-code links, and any referral
                        attribution chain will be affected.
                    </p>
                    <button
                        type="button"
                        onClick={onDelete}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-klareet-RED text-klareet-RED text-sm hover:bg-klareet-RED/10 transition-colors"
                        data-testid="drawer-delete-btn"
                    >
                        <Trash2 size={14} />
                        Delete this response
                    </button>
                </div>
            ) : null}
        </section>
    );
}

function DeleteModal({ id, onCancel, onDeleted }) {
    const [typed, setTyped] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    async function go() {
        if (typed !== "DELETE") return;
        setBusy(true);
        try {
            await deleteResponse(id);
            onDeleted?.();
        } catch (e) {
            setErr(e?.response?.data?.detail || e?.message || "Delete failed");
            setBusy(false);
        }
    }
    return (
        <motion.div
            key="drawer-delete-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] grid place-items-center px-5 bg-klareet-NAVY/85 backdrop-blur-sm"
        >
            <div
                className="max-w-md w-full rounded-3xl border border-klareet-RED/40 bg-klareet-card p-6"
                data-testid="drawer-delete-modal"
            >
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold tracking-tight text-klareet-TEXT">
                        Delete response permanently?
                    </h3>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="text-klareet-MUT hover:text-klareet-TEXT disabled:opacity-50"
                    >
                        <X size={18} />
                    </button>
                </div>
                <p className="mt-3 text-sm text-klareet-MUT">
                    This cannot be undone. Type{" "}
                    <span className="font-mono text-klareet-RED">DELETE</span>{" "}
                    to confirm.
                </p>
                <input
                    autoFocus
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder="DELETE"
                    className="mt-4 w-full px-4 py-3 rounded-xl border border-klareet-bor bg-klareet-NAVY font-mono text-klareet-TEXT outline-none focus:border-klareet-RED focus:ring-2 focus:ring-klareet-RED/40"
                    data-testid="drawer-delete-input"
                />
                {err ? (
                    <div className="mt-2 text-sm text-klareet-RED font-mono">
                        {err}
                    </div>
                ) : null}
                <div className="mt-5 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={busy}
                        className="inline-flex items-center px-4 py-2 rounded-full border border-klareet-bor text-klareet-MUT hover:text-klareet-TEXT text-sm disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={go}
                        disabled={typed !== "DELETE" || busy}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-klareet-RED text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all"
                        data-testid="drawer-delete-confirm"
                    >
                        <Trash2 size={14} />
                        {busy ? "Deleting…" : "Delete forever"}
                    </button>
                </div>
            </div>
        </motion.div>
    );
}
